<script setup lang="ts">
/**
 * Доска проекта — макет 9:2: колонки 280 с шагом 16, горизонтальный скролл и
 * перемотка к дальнему краю.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { useEventListener, useIntervalFn, useResizeObserver } from '@vueuse/core'
import { AlertTriangle, ChevronsLeft, ChevronsRight, Columns3, SearchX } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import IssueFilterBar from '@/components/IssueFilterBar.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ViewSwitch from '@/components/ViewSwitch.vue'
import BoardColumn from '@/features/board/components/BoardColumn.vue'
import { emptyFilters } from '@/features/board/api'
import { useBoardStore } from '@/features/board/store'
import CreateIssueModal from '@/features/issues/components/CreateIssueModal.vue'
import type { CreatedIssue } from '@/features/issues/api'

/** Как часто доска догоняет чужие переносы */
const REFRESH_INTERVAL = 30_000

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const board = useBoardStore()

const projectKey = computed(() => String(route.params.key ?? ''))

const breadcrumbs = computed(() => [
  { label: board.project?.name || projectKey.value, to: '/projects' },
  { label: t('views.board') },
])

const columnsRef = ref<HTMLElement | null>(null)
const isScrollable = ref(false)
const atEnd = ref(false)

const updateScroll = () => {
  const element = columnsRef.value

  if (!element) return

  const max = element.scrollWidth - element.clientWidth

  isScrollable.value = max > 1
  atEnd.value = element.scrollLeft >= max - 1
}

/** Доска шире экрана уже на пяти колонках: перемотка к дальнему краю. */
const jumpToEdge = () => {
  const element = columnsRef.value

  if (!element) return

  element.scrollTo({ left: atEnd.value ? 0 : element.scrollWidth, behavior: 'smooth' })
}

useEventListener(columnsRef, 'scroll', updateScroll, { passive: true })
useResizeObserver(columnsRef, updateScroll)

const openBoard = async () => {
  await board.open(projectKey.value)

  // «Нет такого проекта» и «есть, но не для вас» — разные экраны, и различает
  // их только ответ сервера.
  const failureRoute = errorRoute(board.error)

  if (failureRoute) await router.replace(failureRoute)
}

const onMove = async (issueId: string, columnId: string, beforeIssueId: string | undefined) => {
  try {
    await board.move(issueId, columnId, beforeIssueId)
  } catch (failure) {
    toast.error(errorMessage(failure, t('board.moveFailed')))
  }
}

const isCreateOpen = ref(false)
/** Из какой колонки позвали создавать; пусто — из шапки экрана */
const createColumnId = ref('')

const openCreate = (columnId = '') => {
  createColumnId.value = columnId
  isCreateOpen.value = true
}

// Доска перечитывается целиком: новая задача встаёт в конец своей колонки, а
// вписывать её в список руками значит гадать про позицию, которую только что
// назначил сервер.
const onCreated = async (issue: CreatedIssue) => {
  toast.success(t('issues.create.created', { key: issue.key }))

  await board.refresh()
}

// Обновление в фоне: соседний человек переносит карточку, и без него два
// экрана расходятся до перезагрузки страницы.
useIntervalFn(() => void board.refresh(), REFRESH_INTERVAL)

onMounted(openBoard)

// Переход между досками из сайдбара меняет только параметр маршрута — экран
// при этом не пересоздаётся.
watch(projectKey, openBoard)
</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template v-if="board.project">
      <ViewSwitch :project-key="board.project.key" />

      <IssueFilterBar
        :filters="board.filters"
        :project-id="board.project.id"
        :type-profile="board.project.typeProfile"
        @change="board.applyFilters"
      />
      <!-- В панели кнопка короткая — «Создать» (макет 34:295): что именно
           создаётся, говорит экран, на котором она стоит. -->
      <AppButton variant="primary" size="small" @click="openCreate()">
        {{ t('toolbar.create') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <div class="board">
    <div v-if="board.isLoading && !board.columns.length" class="board__columns">
      <div v-for="index in 4" :key="index" class="board__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="board.error"
      :icon="AlertTriangle"
      :title="t('board.error.title')"
      :description="t('board.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="openBoard">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="board.isEmpty && board.isFiltered"
      :icon="SearchX"
      :title="t('board.nothingFound.title')"
      :description="t('board.nothingFound.description')"
    >
      <AppButton variant="secondary" @click="board.applyFilters(emptyFilters())">
        {{ t('toolbar.filters.reset') }}
      </AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="board.isEmpty"
      :icon="Columns3"
      :title="t('board.empty.title')"
      :description="t('board.empty.description')"
    >
      <AppButton variant="primary" @click="openCreate()">
        {{ t('issues.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <div v-else ref="columnsRef" class="board__columns">
      <BoardColumn
        v-for="column in board.columns"
        :key="column.id"
        :column="column"
        :is-dragging="board.isDragging"
        @move="onMove"
        @create="openCreate"
        @drag-start="board.beginDrag"
        @drag-end="board.endDrag"
      />
    </div>

    <button
      v-if="isScrollable"
      type="button"
      class="board__jump"
      :title="atEnd ? t('board.scrollToStart') : t('board.scrollToEnd')"
      :aria-label="atEnd ? t('board.scrollToStart') : t('board.scrollToEnd')"
      @click="jumpToEdge"
    >
      <ChevronsLeft v-if="atEnd" :size="18" />
      <ChevronsRight v-else :size="18" />
    </button>
  </div>

  <CreateIssueModal
    v-if="board.project"
    v-model="isCreateOpen"
    :project-id="board.project.id"
    :type-profile="board.project.typeProfile"
    :column-id="createColumnId"
    @created="onCreated"
  />
</template>

<style scoped>
.board {
  /* Ровно остаток экрана под тулбаром: доска не скроллит страницу, скролл
     уходит внутрь — по горизонтали к колонкам, по вертикали внутрь колонки. */
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  overflow: hidden;
}

.board__columns {
  display: flex;
  flex: 1;
  gap: 16px;
  min-height: 0;
  /* 16 по кругу и 16 между колонками — как в макете: первая колонка стоит на
     x=16 доски, последняя не прижимается к правому краю. */
  padding: 16px;
  overflow-x: auto;
  overflow-y: hidden;
}

.board__columns::-webkit-scrollbar {
  height: 8px;
}

.board__columns::-webkit-scrollbar-thumb {
  border-radius: var(--radius-full);
  background: var(--color-border-strong);
}

.board__skeleton {
  flex-shrink: 0;
  width: 280px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.board__jump {
  position: absolute;
  right: 16px;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  box-shadow: var(--shadow-md);
  cursor: pointer;
}

.board__jump:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
</style>
